import { FC, useEffect, useMemo, useRef, useState } from "react";
import { TradeNameCreatorModal } from ".";
import { LoadingStatus } from "../../../@types/enumsGlobal";
import DefaultDictionaryCommandsPanel from "../../../components/commandsPanels/DefaultDictionaryCommandsPanel";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import { Spinner } from "../../../components/spiner/Spinner";
import { ITradeNameByStoreDTO, ITradeNameViewDTO } from "../../../libs/coreapi-dto/dirs/tradeName";
import { TradeNameDataProvider } from "../../../Services/DataProviders/TradeNameDataProvider";
import useGridFilter, { CreateSelectorGridFilter } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { useTestApiContext } from "../../../system/providers/testApiProvider";
import { useUserContext } from "../../../system/providers/userContextProvider";
import { checkAccessStatus } from "../../CommonHelperFunctions";
import { Input } from "antd";
import { t } from "i18next";
import SearchIcon from "../../../components/comboBox/src/Icons/Search";
import GridWrapper from "../../../components/controls/GridWrapper";
import { debounce } from "lodash";

const MIN_SEARCH_LENGTH = 3;

const TradeNameByStoreGrid: FC<IGridProps> = (props) => {
    const appCtx = useAppContext();
    const userContext = useUserContext();
    const testApiContext = useTestApiContext();

    const tradeNameDataProvider = new TradeNameDataProvider(appCtx.coreApiService);
    const [data, setData] = useState<ITradeNameByStoreDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.Completed);
    const [totalCount, setTotalCount] = useState(0);
    const [viewState, setViewState] = useState<GridStateType>("view");
    const [gridFilter, dispatchGridFilter] = useGridFilter(props.baseGridFilter ?? CreateSelectorGridFilter);
    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>(props.data ?? []);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [searchValue, setSearchValue] = useState<string>('');
    const isRealRefresh = useRef(false);

    useEffect(() => {
        setIsSubmitting(true)
        tradeNameDataProvider.getDistinctViewByStore(gridFilter, (data, totalCount) => {
            setData(data)
            setTotalCount(totalCount)
            data.length > 0 ? setLoadingStatus(LoadingStatus.Completed) : setLoadingStatus(LoadingStatus.NoData)
            setIsSubmitting(false)
        })
    }, [gridFilter])
        
        const searchSubstance = (value: string) => {
                if (value.length >= MIN_SEARCH_LENGTH) {
                    const expression = `lower("TradeNameRus") like lower('%${value}%') or lower("TradeNameLat") like lower('%${value}%')`;
                    dispatchGridFilter({ type: "addCustomWhereExpressionFilter", payload: expression });
                } else if (value.length === 0) {
                    dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
                }
            };
        
            const debouncedSearch = useMemo(
                () => debounce(searchSubstance, 1250),
                []
            );
        
            const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setSearchValue(value);
                
                if (props.isModal) {
                    props.onFocusChanged?.(value.length > 0);
                }
                
                if (value.length === 0) {
                    debouncedSearch.cancel();
                    dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
                }
            };
        
            const handlePressEnter = () => {
                debouncedSearch.cancel();
                if (searchValue.length >= MIN_SEARCH_LENGTH || searchValue.length === 0) {
                    searchSubstance(searchValue);
                }
            };
        
            useEffect(() => {
                if (searchValue !== '' && searchValue.length >= MIN_SEARCH_LENGTH) {
                    debouncedSearch(searchValue);
                }
            }, [searchValue]);
        
            useEffect(() => {
                return () => debouncedSearch.cancel();
            }, [debouncedSearch]);
           
            useEffect(() => {
                if (viewState === 'refresh' && isRealRefresh.current) {
                    isRealRefresh.current = false;
                    setSearchValue("");
                    dispatchGridFilter({ type: "deleteCustomWhereExpressionFilter" });
                }
                setViewState('view');
            }, [viewState]);

    return (
        <PluginWrapper>
            {
                checkAccessStatus(props.plugin.permission as IPermission, userContext.userPermission) && <ToolbarWrapper>
                    <DefaultDictionaryCommandsPanel
                        selectedItem={[selectedItem, setSelectedItem]}
                        gridFilter={[gridFilter, dispatchGridFilter]}
                        dataProvider={tradeNameDataProvider}
                        creatorModalJsx={TradeNameCreatorModal}
                        pluginSettings={props.plugin}
                        setViewState={(vs) => setViewState(vs)}
                    />                    
                    <GridWrapper cols={8}>
                        <Input
                            className="custom-input-withPrefix"
                            style={{ width: props.isModal ? 360 : 360 }}
                            value={searchValue}
                            placeholder={t("directory.tradeName.expressionInputPlaceholder")}
                            prefix={<SearchIcon />}
                            onPressEnter={handlePressEnter}
                            onChange={handleSearchChange}
                            onBlur={() => {
                                if (props.isModal) props.onFocusChanged?.(false);
                            }}
                            allowClear
                        />
                    </GridWrapper>
                </ToolbarWrapper>
            }

            {
                isSubmitting ? <Spinner /> : <DefaultGrid
                    showQuantity
                    gridId={props.gridId}
                    data={data}
                    getView={(gridFilter,callback) => {
                        tradeNameDataProvider.getDistinctViewByStore(gridFilter, (e, t) => {
                            callback(e)
                        })
                    }}
                    loadingStatus={loadingStatus}
                    setLoadingStatus={setLoadingStatus}
                    separator
                    singleDirectory
                    totalCount={totalCount}
                    filter={gridFilter}
                    dataProvider={tradeNameDataProvider}
                    plugin={props.plugin}
                    selectedItem={selectedItem}
                    selectedItems={selectedItems}
                    multipleSelect={props.multipleSelect}
                    onSelect={(row) => {
                        setSelectedItem(row)
                        props.onSelect?.(row)
                    }}
                    onMultipleSelect={(rows) => {
                        setSelectedItems(rows)
                        props?.onMultipleSelect?.(rows)
                    }}
                    onDoubleClick={(i) => props.onDoubleClick?.(i)}
                    onSort={(i) => dispatchGridFilter({ type: "sort", payload: i.propertyName })}
                    onFilterDelete={(i) => dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName })}
                    onPageNumberChange={(n) => dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } })}
                    onNumberPerPageChange={(n) => dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } })}
                    onEnter={() => setViewState("edit")}
                />
            }
        </PluginWrapper>
    )
}

export default TradeNameByStoreGrid